<script setup lang="ts">
import Modal from '@/components/common/layout/Modal.vue'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import { useToast } from 'vue-toast-notification'
import { storeToRefs } from 'pinia'
import type { CreateOpportunityDto } from '@/api-swagger/models/CreateOpportunityDto'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { useUserStore } from '@/stores/useUserStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import Datepicker from '@vuepic/vue-datepicker'
import MonetaryInput from '@/components/common/inputs/MonetaryInput.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import RadioInput from '@/components/common/inputs/RadioInput.vue'
import SelectSimple from '@/components/common/selects/SelectSimple.vue'
import { PlusCircleIcon } from '@heroicons/vue/16/solid'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SaveButton from '@/components/common/buttons/SaveButton.vue'
import SelectWithAvatar from '@/components/common/selects/SelectWithAvatar.vue'
import type { Context } from '@/utils/types'
import { useRoute } from 'vue-router'
import { UserRole } from '@/utils/enums'
import { useSelectionsStore } from '@/stores/useSelectionsStore'
import { object, string } from 'yup'
import { useForm } from 'vee-validate'
import { VQuillEditor } from '@morpheme/quill-editor'
import { useAttachmentStore } from '@/stores/useAttachmentStore'
import OpportunityPartnerInfo from '@/components/features/opportunities/OpportunityPartnerInfo.vue'
import IconInfo from '@/assets/icons/info.svg'
import IconWithTooltip from '@/components/common/ui/IconWithTooltip.vue'
import { CheckIcon } from '@heroicons/vue/24/outline'
import FileInput from '@/components/common/inputs/FileInput.vue'
import type { FileInputExpose } from '@/types/fileInput'

const route = useRoute()
const opportunityStore = useOpportunityStore()
const attachmentStore = useAttachmentStore()
const { loading, recurringPlans } = storeToRefs(opportunityStore)
const partnerId: number | null = parseInt(route.params.partnerId as string)
const partnerStore = usePartnerStore()
const { partner, partnerForCreditSafe } = storeToRefs(partnerStore)
const userStore = useUserStore()
const boostCoreStore = useBoostCorePermissionStore()
const { userConnected, users, role, commercials } = storeToRefs(userStore)
const selectionsStore = useSelectionsStore()
const { salesFamilyOpportunitySelections, typeOfSaleOpportunitySelections } =
  storeToRefs(selectionsStore)

const $toast = useToast()
const showModal = ref<boolean>(false)
const opportunity = ref<CreateOpportunityDto>()
const attachmentRef = ref<FileInputExpose | null>(null)

const salesFamilyOptions = computed(() =>
  salesFamilyOpportunitySelections.value.map((item) => ({
    id: item.value,
    value: item.value,
    label: item.name,
  })),
)

const typeOfSaleOptions = computed(() =>
  typeOfSaleOpportunitySelections.value.map((item) => ({
    id: item.value,
    value: item.value,
    label: item.name,
  })),
)

const recurringPlanOptions = computed(() =>
  (recurringPlans.value || []).map((p) => ({
    id: p.id,
    value: p.id,
    label: p.name,
  })),
)

const hasRecurringRevenue = computed(() => (opportunity.value?.recurringRevenue ?? 0) > 0)
const schema = object({
  content: string().required().label('Content'),
})

const { values } = useForm({
  validationSchema: schema,
})

const isReadyforSave = ref(false)

onMounted(() => {
  if (userConnected.value === null) {
    userStore.getUserByEmail(boostCoreStore.user?.email)
  }

  if (!partnerId) {
    partner.value = null
  }

  opportunityStore.getAllRecurringPlans()
})
const errors = ref<Record<string, string>>({})

const requiredFields = [
  { field: 'name', label: "objet de l'opportunité" },
  { field: 'partnerId', label: 'raison sociale' },
  { field: 'salesFamily', label: 'portefeuille' },
  { field: 'userId', label: 'commercial' },
  { field: 'dateDeadline', label: 'date de signature prévue'}
]

const cleanRecurringPlanForSubmit = () => {
  if (!opportunity.value) return
  if (!hasRecurringRevenue.value) {
    opportunity.value.recurringPlanId = null
  }
}

const context = inject<Context>('context', '')

const onSubmit = async () => {
  if (opportunity.value) {
    if (!opportunity.value.userId && userConnected.value) {
      opportunity.value.userId = userConnected.value.id
    }
    opportunity.value.description = values.description
  }

  errors.value = {}
  requiredFields.forEach(({ field, label }) => {
    if (!opportunity.value?.[field as keyof CreateOpportunityDto]) {
      errors.value[field] = `Le champ ${label} est requis.`
    }
  })

  if (hasRecurringRevenue.value && !opportunity.value?.recurringPlanId) {
    errors.value.recurringPlanId = 'La périodicité est requise pour une marge récurrente.'
  }

  if (Object.keys(errors.value).length > 0) {
    return
  }

  cleanRecurringPlanForSubmit()

  if (opportunity.value) {
    await opportunityStore
      .createOpportunity(opportunity.value, context)
      .then((res) => {
        $toast.success('Affaire créée avec succès.')
        if (attachmentRef.value) {
          const files = attachmentRef.value?.fileInputRef?.files

          if (files && files.length > 0) {
            const fileArray: File[] = Array.from(files)

            attachmentStore
              .uploadAttachments(fileArray, 'crm.lead', res?.id || 0)
              .then(() => {
                $toast.success('Pièces jointes enregistrées avec succès.')
              })
              .catch((error: { message: string }) => {
                $toast.error(
                  `Erreur lors de l'enregistrement des pièces jointes : ${error.message}`,
                )
              })
          }
        }
        close()
      })
      .catch((error: { message: string }) => {
        $toast.error(error.message)
      })
  }
}

const openModal = () => {
  const date = new Date();
  isReadyforSave.value = false
  partnerForCreditSafe.value = null
  opportunity.value = {
    name: '',
    expectedRevenue: 0,
    recurringRevenue: 0,
    recurringPlanId: null,
    typeOfSale: 'acquisition',
    salesFamily: '',
    priority: '1',
    dateDeadline: date.toLocaleDateString('en-CA'),
    userId: 0,
    partnerId: partner.value?.id || 0,
    salesEngineer: null,
    description: '',
  }
  showModal.value = true
}

const close = () => {
  showModal.value = false
  opportunity.value = undefined

  isReadyforSave.value = false
  partnerForCreditSafe.value = null
}

const choosenPartner = ref<number | undefined>(undefined)

watch(choosenPartner, (newValue) => {
  isReadyforSave.value = false
  partnerForCreditSafe.value = null
  if (newValue && opportunity.value) {
    opportunity.value.partnerId = newValue
    partnerStore
      .updateScoreByPartnerId(newValue)
      .then(() => {
        $toast.success('Mise à jour du score effectuée')
      })
      .catch(() => {
        $toast.error('Erreur lors de la mise à jour du score')
      })
  }
})
</script>

<template>
  <slot name="trigger" :openModal="openModal">
    <ButtonDaisy text="Nouveau" :icon="PlusCircleIcon" variant="secondary" @click="openModal" />
  </slot>
  <Modal :showModal="showModal" @close="close" title="Création affaire" :loading="loading">
    <template #content>
      <form @submit.prevent="onSubmit" class="space-y-4">
        <div :class="role === UserRole.Manager ? 'grid grid-cols-2 gap-2' : ''">
          <TextInput
            v-model="opportunity!.name"
            label="Objet de l'affaire"
            placeholder="Objet de l'affaire"
            required
            :errorMessage="errors.name"
          />
          <div v-if="role === UserRole.Manager && userConnected">
            <SelectWithAvatar
              label="Commercial"
              v-model="opportunity!.userId"
              :options="(commercials || []).map((c) => ({
                id: c.id,
                text: c.name,
                subText: c.email,
              }))"
              required
              :errorMessage="errors.userId"
            />
          </div>
        </div>

        <OpportunityPartnerInfo
          :is-editable="!partner"
          :partner="partner"
          @update:choosenPartner="choosenPartner = $event"
        ></OpportunityPartnerInfo>
        <div class="grid lg:grid-cols-2 gap-4">
          <div>
            <SelectWithAvatar
              label="Avant-Vente"
              v-model="opportunity!.salesEngineer"
              :options="
                users?.map((u) => ({ id: u.id, text: u.name, subText: u.email || '' })) || []
              "
            />
          </div>
        </div>

        <div class="grid lg:grid-cols-3 gap-4">
          <div>
            <RadioInput
              label="Forecast ⭐⭐⭐"
              :options="[
                { id: '3', value: '3', label: 'Oui' },
                { id: '1', value: '1', label: 'Non' },
              ]"
              v-model="opportunity!.priority"
            />
          </div>
          <div>
            <RadioInput
              label="Type de vente"
              :options="typeOfSaleOptions"
              v-model="opportunity!.typeOfSale"
            />
          </div>
          <div>
            <SelectSimple
              label="Type de portefeuille"
              :options="salesFamilyOptions"
              v-model="opportunity!.salesFamily"
              required
              :errorMessage="errors.salesFamily"
            />
          </div>
        </div>

        <div class="grid lg:grid-cols-3 gap-4">
          <div>
            <label class="label" for="opportunity-deadline">
              <span class="label-text font-semibold">Date de signature prévue *</span>
            </label>
            <Datepicker
              v-model="opportunity!.dateDeadline"
              id="opportunity-deadline"
              locale="fr"
              :enable-time-picker="false"
              auto-apply
              text-input
              required
              format="dd/MM/yyyy"
              min-date="today"
              :disabled-week-days="[6, 0]"
            >
            </Datepicker>
          </div>
          <div>
            <MonetaryInput label="Marge One Shot" v-model="opportunity!.expectedRevenue" />
          </div>
          <div>
            <MonetaryInput label="Marge récurrente" v-model="opportunity!.recurringRevenue" />
          </div>
        </div>
        <div v-if="hasRecurringRevenue" class="grid lg:grid-cols-3 gap-4">
          <div>
            <SelectSimple
              label="Périodicité"
              :options="recurringPlanOptions"
              v-model="opportunity!.recurringPlanId"
              required
              :errorMessage="errors.recurringPlanId"
            />
          </div>
        </div>
        <div>
          <label class="label" for="description">
            <span class="label-text font-semibold"> Description </span>
          </label>
          <VQuillEditor
            name="description"
            v-model="opportunity!.description!"
            placeholder="Description"
          />
        </div>
        <div class="flex justify-between items-end gap-4">
          <FileInput ref="attachmentRef" label="Pièces jointes" :multiple="true" />
          <div
            class="card p-4 flex flex-row items-center gap-4 bg-warning"
            v-if="
              partnerForCreditSafe &&
              partnerForCreditSafe?.saleWarn != 'no-message' &&
              !isReadyforSave
            "
          >
            <div class="tooltip tooltip-top" :data-tip="partnerForCreditSafe?.saleWarnMsg">
              <component :is="IconInfo" class="fill-current text-content" width="20" />
            </div>

            <div class="flex flex-col">
              <p class="text-sm">
                {{ partnerForCreditSafe?.saleWarnMsg }}. Score faible ({{
                  partnerForCreditSafe?.creditSafeValue
                }}).
              </p>
              <p class="text-sm">Veuillez solliciter votre manager.</p>
            </div>
            <IconWithTooltip
              :icon="CheckIcon"
              tooltip="Non Vu"
              variant="soft-accent"
              @click="isReadyforSave = true"
            />
          </div>
          <SaveButton v-else :loading="loading" />
        </div>
      </form>
    </template>
  </Modal>
</template>
